import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Loader2,
  FileText,
  Calendar,
  Building2,
  User,
} from 'lucide-react';
import { onboardingAPI, type FilledDatasetValue, type RiskAssessment, type ApprovalConfidence } from '../../lib/api';
import { toast } from 'sonner';

import { statusColors } from './reviewQuestionnaireUtils';
import type {
  ConditionalLogic,
  Question,
  Section,
  FilledDatasetDetail,
  ApprovalStatusData,
} from './reviewQuestionnaireUtils';
import { ApprovalStatusCard, ScoreRiskCard } from './ReviewCards';
import { ApproveDialog, RejectDialog, MoreInfoDialog } from './ReviewDialogs';
import ReviewResponsesSection from './ReviewResponsesSection';
import ReviewActionBar from './ReviewActionBar';
import { useFormatters } from '../../contexts/PreferencesContext';

export default function ReviewQuestionnaire() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatDate } = useFormatters();

  // CR-6666: Get return URL from location state for proper Back navigation
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [filledDataset, setFilledDataset] = useState<FilledDatasetDetail | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showMoreInfoDialog, setShowMoreInfoDialog] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [approvalConfidence, setApprovalConfidence] = useState<ApprovalConfidence>('no_issues');

  // Risk assessment state
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessment | null>(null);
  const [loadingRisk, setLoadingRisk] = useState(false);

  // Approval status state (multi-level)
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatusData | null>(null);
  const [loadingApproval, setLoadingApproval] = useState(false);

  useEffect(() => {
    if (id) {
      loadFilledDataset();
    }
  }, [id]);

  const loadFilledDataset = async () => {
    try {
      setLoading(true);
      const response = await onboardingAPI.getFilledDataset(Number(id));
      const data = response.data as FilledDatasetDetail;
      setFilledDataset(data);

      if (data.dataset_detail?.sections) {
        setExpandedSections(
          data.dataset_detail.sections.map((s: Section) => `section-${s.id}`)
        );
      }
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      toast.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const fetchRiskAssessment = async () => {
    if (!id) return;
    setLoadingRisk(true);
    try {
      const response = await onboardingAPI.getRiskAssessment(Number(id));
      setRiskAssessment(response.data);
    } catch (error) {
      console.error('Failed to fetch risk assessment:', error);
    } finally {
      setLoadingRisk(false);
    }
  };

  const fetchApprovalStatus = async () => {
    if (!id) return;
    setLoadingApproval(true);
    try {
      const response = await onboardingAPI.getApprovalStatus(Number(id));
      setApprovalStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch approval status:', error);
    } finally {
      setLoadingApproval(false);
    }
  };

  useEffect(() => {
    if (filledDataset && !loading) {
      fetchRiskAssessment();
      fetchApprovalStatus();
    }
  }, [filledDataset?.id, loading]);

  const getAnswerValue = (questionId: number): FilledDatasetValue | undefined => {
    return filledDataset?.values?.find((v) => v.question === questionId);
  };

  const getAnswerForCondition = (questionId: number): string | number | boolean | string[] | undefined => {
    const answer = getAnswerValue(questionId);
    if (!answer) return undefined;
    if (answer.text_value) return answer.text_value;
    if (answer.number_value !== null && answer.number_value !== undefined) return answer.number_value;
    if (answer.boolean_value !== null && answer.boolean_value !== undefined) return answer.boolean_value;
    if (answer.date_value) return answer.date_value;
    if (answer.choice_values && answer.choice_values.length > 0) return answer.choice_values;
    return undefined;
  };

  const evaluateConditionalLogic = (logic?: ConditionalLogic): boolean => {
    if (!logic || !logic.show_if || logic.show_if.length === 0) return true;

    const evaluateCondition = (condition: NonNullable<ConditionalLogic['show_if']>[0]): boolean => {
      const { question_id, operator, value } = condition;
      const answer = getAnswerForCondition(question_id);
      const answerStr = Array.isArray(answer) ? answer.join(',') : String(answer ?? '');
      const answerIsEmpty = answer === undefined || answer === null || answer === '' ||
                           (Array.isArray(answer) && answer.length === 0);

      switch (operator) {
        case 'equals':
          if (Array.isArray(answer)) return answer.includes(String(value));
          return String(answer) === String(value) || answer === value;
        case 'not_equals':
          if (Array.isArray(answer)) return !answer.includes(String(value));
          return String(answer) !== String(value) && answer !== value;
        case 'contains':
          return answerStr.toLowerCase().includes(String(value ?? '').toLowerCase());
        case 'not_contains':
          return !answerStr.toLowerCase().includes(String(value ?? '').toLowerCase());
        case 'is_empty':
          return answerIsEmpty;
        case 'is_not_empty':
          return !answerIsEmpty;
        default:
          return true;
      }
    };

    const logicType = logic.logic || 'and';
    return logicType === 'or'
      ? logic.show_if.some(evaluateCondition)
      : logic.show_if.every(evaluateCondition);
  };

  const shouldShowSection = (section: Section): boolean => evaluateConditionalLogic(section.conditional_logic);
  const shouldShowQuestion = (question: Question): boolean => evaluateConditionalLogic(question.conditional_logic);

  const handleApprove = async () => {
    if (!filledDataset) return;
    try {
      setActionLoading(true);
      const response = await onboardingAPI.approveFilledDataset(filledDataset.id, undefined, approvalConfidence);
      const data = response.data as { next_level?: number };
      if (data.next_level) {
        toast.success(`Approved at current level. Escalated to level ${data.next_level} for final approval.`);
      } else {
        const confidenceMsg = approvalConfidence === 'with_concerns' ? ' (with concerns)' : '';
        toast.success(`Questionnaire approved successfully${confidenceMsg}`);
      }
      setShowApproveDialog(false);
      setApprovalConfidence('no_issues');
      loadFilledDataset();
      fetchApprovalStatus();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve questionnaire');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!filledDataset) return;
    try {
      setActionLoading(true);
      await onboardingAPI.rejectFilledDataset(filledDataset.id, feedback);
      toast.success('Questionnaire rejected');
      setShowRejectDialog(false);
      setFeedback('');
      loadFilledDataset();
      fetchApprovalStatus();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject questionnaire');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestMoreInfo = async () => {
    if (!filledDataset || !feedback.trim()) return;
    try {
      setActionLoading(true);
      await onboardingAPI.requestMoreInfo(filledDataset.id, feedback);
      toast.success('More information requested');
      setShowMoreInfoDialog(false);
      setFeedback('');
      loadFilledDataset();
      fetchApprovalStatus();
    } catch (error) {
      console.error('Failed to request more info:', error);
      toast.error('Failed to request more information');
    } finally {
      setActionLoading(false);
    }
  };

  const canTakeAction = ['submitted', 'under_review', 'more_info_required'].includes(filledDataset?.status || '');

  // CR-5362 Phase 2: roll-up gate for the questionnaire-level Approve
  // button. Mirrors the backend rule — every answer must carry an
  // approved or approved_with_concerns decision before the customer
  // can sign off the whole questionnaire. The backend re-enforces
  // this; the disabled state here is just so the reviewer sees that
  // the action is blocked instead of getting a 400 on click.
  const allValues = filledDataset?.values ?? [];
  const rejectedCount = allValues.filter((v) => v.question_status === 'rejected').length;
  const pendingReviewCount = allValues.filter(
    (v) => !v.question_status || v.question_status === 'pending_review',
  ).length;
  const canFinalApprove = canTakeAction && allValues.length > 0 && rejectedCount === 0 && pendingReviewCount === 0;
  const finalApproveTooltip = !canFinalApprove
    ? rejectedCount > 0
      ? `Cannot approve while ${rejectedCount} question${rejectedCount === 1 ? ' is' : 's are'} rejected. Resolve them first.`
      : pendingReviewCount > 0
      ? `${pendingReviewCount} question${pendingReviewCount === 1 ? '' : 's'} still need a reviewer decision.`
      : undefined
    : undefined;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!filledDataset) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Questionnaire not found</h3>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/onboarding')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Onboarding
        </Button>
      </div>
    );
  }

  const dataset = filledDataset.dataset_detail;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="mb-2"
            onClick={() => {
              // CR-6666: Navigate to the originating page if available, otherwise use history
              if (returnTo) {
                navigate(returnTo);
              } else {
                navigate(-1);
              }
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{filledDataset.dataset_name || 'Review Questionnaire'}</h1>
          <p className="text-gray-600">{dataset?.description}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={statusColors[filledDataset.status] || 'bg-gray-100'}>
            {filledDataset.status.replace('_', ' ')}
          </Badge>
          {canTakeAction && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowRejectDialog(true)} className="text-red-600 border-red-200 hover:bg-red-50">
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button
                size="sm"
                onClick={() => setShowApproveDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                disabled={!canFinalApprove}
                title={finalApproveTooltip}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Approve
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Supplier Info Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Submission Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Supplier</p>
                <p className="font-medium">{filledDataset.supplier_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Submitted</p>
                <p className="font-medium">
                  {filledDataset.submitted_at
                    ? formatDate(filledDataset.submitted_at)
                    : 'Not submitted'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Progress</p>
                <p className="font-medium">
                  {filledDataset.questions_answered} of {filledDataset.questions_total} answered
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Completion</p>
                <p className="font-medium">{filledDataset.completion_percentage}%</p>
              </div>
            </div>
          </div>
          <Progress value={filledDataset.completion_percentage} className="mt-4" />
        </CardContent>
      </Card>

      {/* Multi-Level Approval Status Card */}
      {approvalStatus && (
        <ApprovalStatusCard approvalStatus={approvalStatus} loadingApproval={loadingApproval} />
      )}

      {/* Score Summary Card */}
      {riskAssessment && (
        <ScoreRiskCard riskAssessment={riskAssessment} loadingRisk={loadingRisk} />
      )}

      {/* Questions and Answers */}
      <Card>
        <CardHeader>
          <CardTitle>Responses</CardTitle>
          <CardDescription>
            Review the supplier&apos;s responses below.
            {/* CR-5362 Phase 2: progress chips for the per-question
                review rollup. Helps the reviewer see at a glance how
                many questions still need their decision before the
                whole questionnaire can be approved. */}
            {canTakeAction && allValues.length > 0 && (
              <span className="ml-2 inline-flex flex-wrap gap-2 align-middle">
                {pendingReviewCount > 0 && (
                  <Badge variant="outline" className="bg-gray-100 text-gray-700">
                    {pendingReviewCount} pending
                  </Badge>
                )}
                {rejectedCount > 0 && (
                  <Badge variant="outline" className="bg-red-100 text-red-800">
                    {rejectedCount} rejected
                  </Badge>
                )}
                {pendingReviewCount === 0 && rejectedCount === 0 && (
                  <Badge variant="outline" className="bg-green-100 text-green-800">
                    All questions reviewed
                  </Badge>
                )}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ReviewResponsesSection
            sections={dataset?.sections ?? []}
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            shouldShowSection={shouldShowSection}
            shouldShowQuestion={shouldShowQuestion}
            getAnswerValue={getAnswerValue}
            canTakeAction={canTakeAction}
            reload={loadFilledDataset}
          />
        </CardContent>

        {/* Action Buttons */}
        {canTakeAction && (
          <CardFooter className="flex justify-end gap-3 border-t pt-6">
            <Button variant="outline" onClick={() => setShowMoreInfoDialog(true)}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Request More Info
            </Button>
            <Button variant="destructive" onClick={() => setShowRejectDialog(true)}>
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => setShowApproveDialog(true)}
              className="bg-green-600 hover:bg-green-700"
              disabled={!canFinalApprove}
              title={finalApproveTooltip}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </CardFooter>
        )}

        {/* Status messages */}
        {filledDataset.status === 'approved' && (
          <CardFooter className="border-t pt-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              <span>This questionnaire has been approved.</span>
            </div>
          </CardFooter>
        )}
        {filledDataset.status === 'rejected' && (
          <CardFooter className="border-t pt-4">
            <div className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              <span>This questionnaire has been rejected.</span>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Dialogs */}
      <ApproveDialog
        open={showApproveDialog}
        onOpenChange={setShowApproveDialog}
        approvalConfidence={approvalConfidence}
        onConfidenceChange={setApprovalConfidence}
        onApprove={handleApprove}
        actionLoading={actionLoading}
      />
      <RejectDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        feedback={feedback}
        onFeedbackChange={setFeedback}
        onReject={handleReject}
        actionLoading={actionLoading}
      />
      <MoreInfoDialog
        open={showMoreInfoDialog}
        onOpenChange={setShowMoreInfoDialog}
        feedback={feedback}
        onFeedbackChange={setFeedback}
        onRequestMoreInfo={handleRequestMoreInfo}
        actionLoading={actionLoading}
      />

      {canTakeAction && (
        <ReviewActionBar
          filledDatasetStatus={filledDataset.status}
          riskAssessment={riskAssessment}
          canFinalApprove={canFinalApprove}
          finalApproveTooltip={finalApproveTooltip}
          onApproveClick={() => setShowApproveDialog(true)}
          onRejectClick={() => setShowRejectDialog(true)}
          onMoreInfoClick={() => setShowMoreInfoDialog(true)}
        />
      )}
    </div>
  );
}
